/**
 * Upload Database Loader
 *
 * Handles loading, validating, caching, and exporting of user-uploaded SQLite databases.
 */

import { saveToIndexedDb, getFromIndexedDb, deleteFromIndexedDb } from './demoDatabaseLoaderInternal';
import { AUTO_CHECKPOINT_THRESHOLD } from './demoDatabaseLoader';

/** Accepted file extensions */
export const ACCEPTED_EXTENSIONS = ['.sqlite', '.db', '.sqlite3'];

/** Warning threshold for large files (50 MB) */
export const LARGE_FILE_WARNING_THRESHOLD = 50 * 1024 * 1024;

/** Maximum file size allowed (200 MB) */
export const MAX_FILE_SIZE = 200 * 1024 * 1024;

/** SQLite file magic bytes (first 16 bytes of header) */
const SQLITE_MAGIC = 'SQLite format 3\x00';

/** Prefix for uploaded database cache keys */
const UPLOAD_CACHE_PREFIX = 'upload:';

export type UploadValidationResult =
  | { valid: true }
  | { valid: false; error: string };

export type FileSizeCheck =
  | { allowed: true; warning?: string }
  | { allowed: false; error: string };

/**
 * Check if a file has an accepted SQLite extension.
 */
export function hasValidExtension(filename: string): boolean {
  const lower = filename.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * Check if a file size is allowed.
 */
export function checkFileSize(size: number): FileSizeCheck {
  if (size > MAX_FILE_SIZE) {
    const maxMb = Math.round(MAX_FILE_SIZE / (1024 * 1024));
    const sizeMb = Math.round(size / (1024 * 1024));
    return {
      allowed: false,
      error: `File is too large (${sizeMb} MB). Maximum allowed size is ${maxMb} MB.`,
    };
  }

  if (size > LARGE_FILE_WARNING_THRESHOLD) {
    const sizeMb = Math.round(size / (1024 * 1024));
    const warningMb = Math.round(LARGE_FILE_WARNING_THRESHOLD / (1024 * 1024));
    return {
      allowed: true,
      warning: `Large file (${sizeMb} MB). Files over ${warningMb} MB may load slowly and won't auto-save changes.`,
    };
  }

  return { allowed: true };
}

/**
 * Validate that an ArrayBuffer contains valid SQLite data.
 * Checks the SQLite header magic bytes.
 */
export function validateSqliteHeader(buffer: ArrayBuffer): UploadValidationResult {
  if (buffer.byteLength < 100) {
    return { valid: false, error: 'File is too small to be a valid SQLite database.' };
  }

  // Check SQLite magic bytes (first 16 bytes)
  const header = new Uint8Array(buffer, 0, 16);
  const headerStr = String.fromCharCode(...header);

  if (headerStr !== SQLITE_MAGIC) {
    return {
      valid: false,
      error: 'This file does not appear to be a valid SQLite database. Please select a .sqlite, .db, or .sqlite3 file.',
    };
  }

  return { valid: true };
}

/**
 * Read a File object into an ArrayBuffer.
 */
export async function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Generate a unique ID for an uploaded database based on the filename.
 */
export function generateUploadId(filename: string): string {
  // Use filename + timestamp to ensure uniqueness
  const timestamp = Date.now();
  const safeName = filename.replace(/[^a-zA-Z0-9.-]/g, '_');
  return `upload_${safeName}_${timestamp}`;
}

/**
 * Get the cache key for an uploaded database.
 */
function getUploadCacheKey(uploadId: string): string {
  return `${UPLOAD_CACHE_PREFIX}${uploadId}`;
}

/**
 * Save uploaded database to IndexedDB for persistence.
 */
export async function cacheUploadedDatabase(
  uploadId: string,
  data: ArrayBuffer,
  force: boolean = false
): Promise<{ success: boolean; reason?: string }> {
  const size = data.byteLength;

  // Skip auto-checkpoint for large databases unless forced
  if (!force && size > AUTO_CHECKPOINT_THRESHOLD) {
    return {
      success: false,
      reason: 'Database too large for auto-save. Use Export to save changes.',
    };
  }

  const key = getUploadCacheKey(uploadId);
  const success = await saveToIndexedDb(key, data);

  if (success) {
    console.log('[UploadLoader] Cached uploaded database:', uploadId, `(${size} bytes)`);
    return { success: true };
  }

  return { success: false, reason: 'Failed to save to storage' };
}

/**
 * Load a previously cached uploaded database.
 */
export async function loadCachedUploadedDatabase(uploadId: string): Promise<ArrayBuffer | null> {
  const key = getUploadCacheKey(uploadId);
  return getFromIndexedDb(key);
}

/**
 * Delete a cached uploaded database.
 */
export async function deleteCachedUploadedDatabase(uploadId: string): Promise<boolean> {
  const key = getUploadCacheKey(uploadId);
  return deleteFromIndexedDb(key);
}

/**
 * List all cached uploaded database IDs from IndexedDB.
 */
export async function listCachedUploadIds(): Promise<string[]> {
  // This would require enumerating all keys in IndexedDB
  // For now, we'll track uploads in the browser mode store instead
  return [];
}

/**
 * Export database as a downloadable file.
 */
export function downloadDatabase(data: Uint8Array, filename: string): void {
  const blob = new Blob([data], { type: 'application/x-sqlite3' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.sqlite') ? filename : `${filename}.sqlite`;
  document.body.appendChild(a);
  a.click();

  // Cleanup
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Format file size for display.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
